/**
 * AppSync Events WebSocket Service
 *
 * Core WebSocket service for connecting to AWS AppSync Events API.
 * Handles connection management, subscriptions, and reconnection logic.
 */

import type {
  AppSyncOutgoingMessage,
  PendingSubscription,
  ReconnectionConfig,
  SubscriptionInfo,
  SubscriptionOptions,
  WebSocketServiceConfig,
} from '@/types/websocket';
import {
  parseEventData,
  parseIncomingMessage,
  WebSocketConnectionState,
} from '@/types/websocket';

import {
  APPSYNC_SUBPROTOCOL,
  CONNECTION_TIMEOUT_MS,
  DEFAULT_RECONNECTION_CONFIG,
  getCloseCodeMessage,
} from './constants';

// ------------- Helper Functions -------------

/**
 * Generate a unique subscription ID
 */
function generateSubscriptionId(): string {
  return `sub-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Encode string to base64URL (base64 with URL-safe characters)
 * Replaces + with -, / with _, and removes trailing = padding
 */
function base64UrlEncode(str: string): string {
  // In browser, use btoa for base64 encoding
  const base64 = btoa(str);
  // Convert to base64URL: replace + with -, / with _, remove = padding
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Calculate reconnection delay with exponential backoff and jitter
 */
function calculateReconnectDelay(attempt: number, config: ReconnectionConfig): number {
  const exponentialDelay = Math.min(
    config.initialDelay * 2 ** attempt,
    config.maxDelay,
  );
  // Add random jitter (0-1000ms) to prevent thundering herd
  const jitter = Math.random() * 1000;
  return exponentialDelay + jitter;
}

// ------------- AppSync Events Service Class -------------

/**
 * AppSync Events WebSocket Service
 *
 * Manages WebSocket connection to AWS AppSync Events API.
 *
 * Connection Flow:
 * 1. Create WebSocket with aws-appsync-event-ws subprotocol
 * 2. On open → send {"type": "connection_init"}
 * 3. On connection_ack → mark connected, subscribe to channels
 * 4. Send subscribe message with channel and authorization
 * 5. On subscribe_success → subscription active
 * 6. On event → parse JSON string, dispatch to handlers
 */
export class AppSyncEventsService {
  private ws: WebSocket | null = null;
  private config: WebSocketServiceConfig;
  private reconnectionConfig: ReconnectionConfig;
  private connectionState: WebSocketConnectionState = WebSocketConnectionState.DISCONNECTED;
  private subscriptions: Map<string, SubscriptionInfo> = new Map();
  private pendingSubscriptions: PendingSubscription[] = [];
  private subscriptionsToResubscribe: Set<string> = new Set();
  private reconnectAttempts = 0;
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private connectionTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private connectPromise: Promise<void> | null = null;
  private isIntentionalDisconnect = false;
  private lastCloseCode: number | null = null;
  private lastCloseReason: string = '';

  constructor(
    config: WebSocketServiceConfig,
    reconnectionConfig: ReconnectionConfig = DEFAULT_RECONNECTION_CONFIG,
  ) {
    this.config = config;
    this.reconnectionConfig = reconnectionConfig;
  }

  // ------------- Public Methods -------------

  /**
   * Connect to the WebSocket server
   * Returns existing promise if connection is already in progress
   */
  async connect(): Promise<void> {
    // Return existing promise if connection is in progress
    if (this.connectPromise !== null) {
      return this.connectPromise;
    }

    if (this.connectionState === WebSocketConnectionState.CONNECTED) {
      return;
    }

    this.isIntentionalDisconnect = false;

    this.connectPromise = this.performConnect();

    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  /**
   * Internal connection logic
   */
  private async performConnect(): Promise<void> {
    this.setConnectionState(WebSocketConnectionState.CONNECTING);

    try {
      // Get fresh auth token
      const authToken = await this.config.getAuthToken();
      if (authToken === '') {
        throw new Error('No authentication token available');
      }

      // Build authorization header for subprotocol
      // AppSync Events uses two subprotocols:
      // 1. aws-appsync-event-ws - protocol identifier
      // 2. header-{base64url encoded auth} - authentication headers
      const url = new URL(this.config.url);
      const authHeader = JSON.stringify({
        host: url.host,
        authorization: `Bearer ${authToken}`,
      });
      const encodedAuth = base64UrlEncode(authHeader);
      const headerSubprotocol = `header-${encodedAuth}`;

      // Create WebSocket with both subprotocols
      this.ws = new WebSocket(this.config.url, [APPSYNC_SUBPROTOCOL, headerSubprotocol]);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);

      // Start connection timeout
      this.startConnectionTimeout();
    } catch (error) {
      this.clearConnectionTimeout();
      this.setConnectionState(WebSocketConnectionState.ERROR);
      this.config.onError?.(error instanceof Error ? error : new Error(String(error)));
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.isIntentionalDisconnect = true;
    this.clearReconnectTimeout();
    this.clearConnectionTimeout();

    if (this.ws !== null) {
      // Unsubscribe from all channels
      for (const [id] of this.subscriptions) {
        this.sendUnsubscribe(id);
      }
      this.subscriptions.clear();
      this.pendingSubscriptions = [];
      this.subscriptionsToResubscribe.clear();

      this.ws.close();
      this.ws = null;
    }

    this.setConnectionState(WebSocketConnectionState.DISCONNECTED);
    this.reconnectAttempts = 0;
    this.lastCloseCode = null;
    this.lastCloseReason = '';
  }

  /**
   * Subscribe to a channel
   *
   * @returns Subscription ID for later unsubscription
   */
  async subscribe(options: SubscriptionOptions): Promise<string> {
    const subscriptionId = generateSubscriptionId();

    const subscriptionInfo: SubscriptionInfo = {
      ...options,
      id: subscriptionId,
    };

    // If connected, subscribe immediately
    if (this.connectionState === WebSocketConnectionState.CONNECTED) {
      this.subscriptions.set(subscriptionId, subscriptionInfo);
      await this.sendSubscribe(subscriptionInfo);
    } else {
      // Queue with pre-generated ID for consistent ID across reconnects
      const pendingSubscription: PendingSubscription = {
        ...options,
        id: subscriptionId,
      };
      this.pendingSubscriptions.push(pendingSubscription);

      // Start connection if not already
      if (
        this.connectionState === WebSocketConnectionState.DISCONNECTED
        || this.connectionState === WebSocketConnectionState.ERROR
      ) {
        await this.connect();
      }
    }

    return subscriptionId;
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(subscriptionId: string): void {
    const subscription = this.subscriptions.get(subscriptionId);
    if (subscription === undefined) {
      return;
    }

    this.sendUnsubscribe(subscriptionId);
    this.subscriptions.delete(subscriptionId);
    this.subscriptionsToResubscribe.delete(subscriptionId);
  }

  /**
   * Get current connection state
   */
  getConnectionState(): WebSocketConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === WebSocketConnectionState.CONNECTED;
  }

  // ------------- Private Methods - Timeout Management -------------

  private startConnectionTimeout(): void {
    this.clearConnectionTimeout();
    this.connectionTimeoutId = setTimeout(() => {
      this.handleConnectionTimeout();
    }, CONNECTION_TIMEOUT_MS);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutId !== null) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = null;
    }
  }

  private handleConnectionTimeout(): void {
    // Close the socket if still open
    if (this.ws !== null) {
      this.ws.close();
      this.ws = null;
    }

    const error = this.buildContextError('Connection timeout - server did not acknowledge connection');
    this.setConnectionState(WebSocketConnectionState.ERROR);
    this.config.onError?.(error);
    this.scheduleReconnect();
  }

  // ------------- Private Methods - Event Handlers -------------

  private handleOpen(): void {
    this.sendConnectionInit();
  }

  private handleMessage(event: MessageEvent): void {
    const messageData = typeof event.data === 'string' ? event.data : String(event.data);
    const message = parseIncomingMessage(messageData);
    if (message === null) {
      return;
    }

    switch (message.type) {
      case 'connection_ack':
        this.handleConnectionAck();
        break;
      case 'connection_error':
        this.handleConnectionError(message.errors);
        break;
      case 'ka':
        // Keep-alive, no action needed
        break;
      case 'subscribe_success':
        this.handleSubscribeSuccess(message.id);
        break;
      case 'subscribe_error':
        this.handleSubscribeError(message.id, message.errors);
        break;
      case 'event':
      case 'data':
        // Both 'event' and 'data' message types contain event payload
        this.handleEvent(message.id, message.event);
        break;
      case 'error':
        this.handleServerError(message.errors);
        break;
      default:
        // Unhandled message type
        break;
    }
  }

  private handleClose(event: CloseEvent): void {
    // Store close event details for error context
    this.lastCloseCode = event.code;
    this.lastCloseReason = event.reason;

    this.clearConnectionTimeout();
    this.ws = null;

    if (this.isIntentionalDisconnect === false) {
      // Save subscription IDs for resubscription on reconnect
      this.subscriptionsToResubscribe = new Set(this.subscriptions.keys());
      this.scheduleReconnect();
    } else {
      this.setConnectionState(WebSocketConnectionState.DISCONNECTED);
    }
  }

  private handleError(_event: Event): void {
    // Browser WebSocket error events don't expose error details (security)
    // Use the last close code/reason for context
    const error = this.buildContextError('WebSocket connection error');
    this.config.onError?.(error);
  }

  // ------------- Private Methods - Message Handlers -------------

  private handleConnectionAck(): void {
    this.clearConnectionTimeout();
    this.setConnectionState(WebSocketConnectionState.CONNECTED);
    this.reconnectAttempts = 0;
    this.lastCloseCode = null;
    this.lastCloseReason = '';

    // Process pending subscriptions
    this.processPendingSubscriptions();
  }

  private handleConnectionError(
    errors: Array<{ errorCode: number; message: string }> | undefined,
  ): void {
    this.clearConnectionTimeout();

    const errorCode = errors?.[0]?.errorCode ?? 0;
    const errorMessage = errors?.[0]?.message ?? 'Unknown connection error';

    // Mark as intentional to prevent handleClose from scheduling reconnect
    // Connection errors are usually auth-related - retrying won't help
    this.isIntentionalDisconnect = true;

    // Close socket if still open
    if (this.ws !== null) {
      this.ws.close();
      this.ws = null;
    }

    this.setConnectionState(WebSocketConnectionState.ERROR);
    this.config.onError?.(new Error(`Connection error (${errorCode}): ${errorMessage}`));
  }

  private handleSubscribeSuccess(_subscriptionId: string): void {
    // Subscription confirmed
  }

  private handleSubscribeError(
    subscriptionId: string,
    errors: Array<{ errorType: string; message: string }> | undefined,
  ): void {
    const errorMessage = errors?.[0]?.message ?? 'Unknown subscription error';

    const subscription = this.subscriptions.get(subscriptionId);
    if (subscription !== undefined) {
      subscription.onError?.(new Error(errorMessage));
      this.subscriptions.delete(subscriptionId);
    }
  }

  private handleEvent(subscriptionId: string, eventData: string): void {
    const subscription = this.subscriptions.get(subscriptionId);
    if (subscription === undefined) {
      return;
    }

    const event = parseEventData(eventData);
    if (event === null) {
      return;
    }

    subscription.onEvent(event);
  }

  private handleServerError(errors: Array<{ errorType: string; message: string }> | undefined): void {
    const errorMessage = errors?.[0]?.message ?? 'Unknown server error';
    this.config.onError?.(new Error(errorMessage));
  }

  // ------------- Private Methods - Send Messages -------------

  private sendConnectionInit(): void {
    this.sendMessage({ type: 'connection_init' });
  }

  private async sendSubscribe(subscription: SubscriptionInfo): Promise<void> {
    const authToken = await this.config.getAuthToken();

    // Note: AppSync Events expects lowercase header keys in authorization object
    this.sendMessage({
      type: 'subscribe',
      id: subscription.id,
      channel: subscription.channel,
      authorization: {
        host: new URL(this.config.url).host,
        authorization: `Bearer ${authToken}`,
      },
    });
  }

  private sendUnsubscribe(subscriptionId: string): void {
    this.sendMessage({
      type: 'unsubscribe',
      id: subscriptionId,
    });
  }

  private sendMessage(message: AppSyncOutgoingMessage): boolean {
    if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      const jsonString = JSON.stringify(message);
      this.ws.send(jsonString);
      return true;
    } catch (error) {
      const sendError = error instanceof Error
        ? error
        : new Error(`Failed to serialize/send message: ${String(error)}`);
      this.config.onError?.(sendError);
      return false;
    }
  }

  // ------------- Private Methods - Utilities -------------

  private setConnectionState(state: WebSocketConnectionState): void {
    if (this.connectionState === state) {
      return;
    }

    this.connectionState = state;
    this.config.onConnectionStateChange?.(state);
  }

  private async processPendingSubscriptions(): Promise<void> {
    // Process pending subscriptions (use their pre-generated IDs)
    const pending = [...this.pendingSubscriptions];
    this.pendingSubscriptions = [];

    for (const pendingSubscription of pending) {
      const subscriptionInfo: SubscriptionInfo = {
        channel: pendingSubscription.channel,
        onEvent: pendingSubscription.onEvent,
        onError: pendingSubscription.onError,
        id: pendingSubscription.id,
      };
      this.subscriptions.set(pendingSubscription.id, subscriptionInfo);
      await this.sendSubscribe(subscriptionInfo);
    }

    // Re-subscribe only the subscriptions that need resubscription (from previous connection)
    // This prevents duplicate subscriptions
    const idsToResubscribe = new Set(this.subscriptionsToResubscribe);
    this.subscriptionsToResubscribe.clear();

    for (const subscriptionId of idsToResubscribe) {
      const subscription = this.subscriptions.get(subscriptionId);
      if (subscription !== undefined) {
        await this.sendSubscribe(subscription);
      }
    }
  }

  private buildContextError(baseMessage: string): Error {
    const parts: string[] = [baseMessage];

    if (this.lastCloseCode !== null) {
      parts.push(`Close code: ${this.lastCloseCode} (${getCloseCodeMessage(this.lastCloseCode)})`);
    }

    if (this.lastCloseReason !== '') {
      parts.push(`Reason: ${this.lastCloseReason}`);
    }

    if (this.reconnectAttempts > 0) {
      parts.push(`Reconnect attempt: ${this.reconnectAttempts}/${this.reconnectionConfig.maxAttempts}`);
    }

    return new Error(parts.join(' | '));
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.reconnectionConfig.maxAttempts) {
      this.setConnectionState(WebSocketConnectionState.ERROR);
      this.config.onError?.(this.buildContextError('Max reconnection attempts reached'));
      return;
    }

    this.setConnectionState(WebSocketConnectionState.RECONNECTING);
    this.clearReconnectTimeout();

    const delay = calculateReconnectDelay(this.reconnectAttempts, this.reconnectionConfig);

    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect().catch(() => {});
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }
}

// ------------- Factory Function -------------

/**
 * Create an AppSync Events WebSocket service instance
 */
export function createAppSyncEventsService(
  config: WebSocketServiceConfig,
  reconnectionConfig?: ReconnectionConfig,
): AppSyncEventsService {
  return new AppSyncEventsService(config, reconnectionConfig);
}
